import type { Integration } from '@detect/shared';
import { api, withTenant, type PaginatedResult } from './api';
import type { WithId } from './types';

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

/** What the API RETURNS (#48): the stored config minus the internal secret
 *  names, plus `*Set` flags (is a value on file?) and `secretsNeedReentry`
 *  (a pre-#48 credential that must be re-entered before it will deliver).
 *  Secret VALUES are never returned. */
export type IntegrationView = DistributiveOmit<
  Integration,
  'serviceTokenSecretName' | 'apiKeySecretName' | 'hmacSecretName' | 'secretHeaderSecretName' | 'deskTenantId' | 'secretsPurgedAt' | 'writeLeaseUntil' | 'writeLeaseOwner' | 'deskTenantIdDomain' | 'purgeLeaseUntil' | 'purgeLeaseOwner' | 'purgeDirtyAt'
> & { secretsNeedReentry: boolean; secretHeaderSet?: boolean; deskWorkspace?: string };

export const integrationService = {
  list: (
    tenantId: string,
    params: { limit?: number; startAfter?: string } = {}
  ): Promise<PaginatedResult<WithId<IntegrationView>>> =>
    api.getPaginated<WithId<IntegrationView>>(
      withTenant('/integrations', tenantId, params)
    ),

  create: (
    tenantId: string,
    input: Record<string, unknown>
  ): Promise<WithId<IntegrationView>> =>
    api.post<WithId<IntegrationView>>(withTenant('/integrations', tenantId), input),

  update: (
    tenantId: string,
    id: string,
    patch: Record<string, unknown>
  ): Promise<WithId<IntegrationView>> =>
    api.put<WithId<IntegrationView>>(withTenant(`/integrations/${id}`, tenantId), patch),

  remove: (tenantId: string, id: string): Promise<void> =>
    api.delete(withTenant(`/integrations/${id}`, tenantId)),
};
